import { useCallback, useEffect, useState } from "react";
import { Check, FileSpreadsheet, FileText, Loader2, Unlink, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { lierDoublon } from "@/components/DoublonLien";
import { api, download, errMsg, money, telechargerExcel } from "@/lib/api";

const Op = ({ o, lib }) => (
  <span className="block text-xs"><span className="font-mono text-slate-500">{lib} {o.date}</span> · {o.description} · <span className="font-mono">{money(o.montant)}</span></span>
);

const Suggestion = ({ s, i, onChange }) => {
  const confirmer = () => lierDoublon(s.paiement.id, s.remboursement.id, onChange).then(() => onChange()).catch((e) => toast.error(errMsg(e)));
  const ignorer = () => api.post("/doublons/ignorer", { paiement_id: s.paiement.id, remboursement_id: s.remboursement.id }).then(() => onChange()).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-3 sm:flex-row sm:items-center" data-testid={`doublon-suggestion-${i}`}>
      <div className="flex-1 min-w-0 space-y-0.5">
        <Op o={s.original} lib="Payé le" /><Op o={s.paiement} lib="Payé encore le" /><Op o={s.remboursement} lib="Remboursé le" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={confirmer} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`doublon-suggestion-${i}-confirmer`}><Check className="h-3.5 w-3.5 mr-1" />Confirmer</Button>
        <Button size="sm" variant="outline" onClick={ignorer} data-testid={`doublon-suggestion-${i}-ignorer`}><X className="h-3.5 w-3.5 mr-1" />Ce n'est pas un doublon</Button>
      </div>
    </div>
  );
};

const annees = (d) => {
  const dates = [...d.paires.flatMap((p) => [p.paiement, p.remboursement]), ...d.suggestions.flatMap((s) => [s.paiement, s.remboursement])].filter(Boolean).map((o) => o.date.slice(0, 4));
  return [...new Set([String(new Date().getFullYear()), ...dates])].sort().reverse();
};

// PDF pour le comptable : choix de l'année puis téléchargement
const PdfComptable = ({ d }) => {
  const liste = annees(d);
  const [annee, setAnnee] = useState(liste[0]);
  const [envoi, setEnvoi] = useState(false);
  const telecharger = async () => {
    setEnvoi(true);
    try { await download("/doublons/pdf", { annee }, `Paiements en double rembourses ${annee}.pdf`); } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  const excel = async () => {
    const dans = (c) => [c.paiement, c.remboursement].some((o) => o?.date?.startsWith(annee));
    const m = (v) => ({ v: v ?? null, f: "money" });
    const ligne = (statut, c) => [{ v: statut }, { v: c.paiement?.date || "" }, { v: c.paiement?.description || "" }, m(c.paiement?.montant), { v: c.remboursement?.date || "" }, m(c.remboursement?.montant)];
    const lignes = [...d.paires.filter(dans).map((c) => ligne("Lié", c)), ...d.suggestions.filter(dans).map((c) => ligne("À vérifier", c))];
    setEnvoi(true);
    try { await telechargerExcel(`Paiements en double ${annee}`, { colonnes: ["Statut", "Payé en double le", "Description", "Montant", "Remboursé le", "Montant remboursé"], lignes }); }
    catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  return (
    <div className="flex shrink-0 items-center gap-2">
      <Select value={annee} onValueChange={setAnnee}>
        <SelectTrigger className="h-8 w-24" data-testid="doublons-pdf-annee"><SelectValue /></SelectTrigger>
        <SelectContent>{liste.map((a) => <SelectItem key={a} value={a} data-testid={`doublons-pdf-annee-${a}`}>{a}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={telecharger} disabled={envoi} data-testid="doublons-pdf">
        {envoi ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}<span className="hidden sm:inline">PDF pour le comptable</span><span className="sm:hidden">PDF</span>
      </Button>
      <Button size="sm" variant="outline" onClick={excel} disabled={envoi} data-testid="doublons-excel"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
    </div>
  );
};

// Paires « paiement en double + remboursement » proposées et liées, exclues de tous les totaux
export const DoublonsPanel = ({ version, onChange }) => {
  const [d, setD] = useState(null);
  const charger = useCallback(() => { api.get("/doublons").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { charger(); }, [charger, version]);
  const maj = () => { charger(); onChange(); };
  if (!d) return null;
  const delier = (id) => api.delete(`/doublons/${id}`).then(() => { toast.success("Lien retiré"); maj(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Panel title="Paiements en double remboursés" testId="doublons-panel" action={<PdfComptable d={d} />}>
      <p className="mb-3 text-xs text-slate-500">Un paiement fait 2 fois puis remboursé : le 2e paiement et son remboursement restent visibles, mais ne comptent plus dans les retraits, les dépôts, la TPS/TVQ, le tableau de bord ni les rapports. Proposés : même bénéficiaire et même montant payés 2 fois en 31 jours, puis un dépôt du même montant dans les 6 mois. Vous pouvez aussi lier une opération avec son bouton « Doublon remboursé ».</p>
      {d.suggestions.length === 0 && d.paires.length === 0 && <p className="text-sm text-slate-500" data-testid="doublons-vide">Aucun paiement en double repéré.</p>}
      <div className="space-y-2">{d.suggestions.map((s, i) => <Suggestion key={`${s.paiement.id}-${s.remboursement.id}`} s={s} i={i} onChange={maj} />)}</div>
      {d.paires.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="doublons-paires">
          <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Liés ({d.paires.length})</p>
          {d.paires.map((p) => (
            <div key={p.id} className="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-600 sm:flex-row sm:items-center" data-testid={`doublon-paire-${p.id}`}>
              <div className="flex-1 min-w-0 space-y-0.5">{p.paiement && <Op o={p.paiement} lib="Payé en double le" />}{p.remboursement && <Op o={p.remboursement} lib="Remboursé le" />}</div>
              <Button size="sm" variant="outline" onClick={() => delier(p.id)} data-testid={`doublon-paire-${p.id}-delier`}><Unlink className="h-3.5 w-3.5 mr-1" />Délier</Button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
